import React from 'react'
import { Alert, Box, Button, TextField, Typography } from '@mui/material'
import { useNamesTranslation } from '../hooks/useNamesTranslation'
import { revertDraftState } from '../model/revertPlan'
import UiMessageAlert from './UiMessageAlert'

/**
 * The confirmation of a revert, inline under the buttons of the detail (RN11, maqueta `.confirm`, no dialog): the reason (the
 * ones of reverting), the comment, the notes of the action and the two buttons. `revert` is the state `useHistory` holds
 * (`reasonCode`, `comment`, `sending`, `message`, `field`); `reasons` = `{list, status}`. While sending everything is locked and
 * the button reads «Reverting…». No error closes it nor empties the reason or the comment.
 */
const RevertConfirmation = ({ action, revert, reasons, onReason, onComment, onConfirm, onCancel, onRetry, renderMessage }) => {
  const { t_ } = useNamesTranslation()
  const { sending } = revert
  const draft = revertDraftState(revert, reasons.list)
  const failed = reasons.status === 'failed'
  const { comment } = draft
  const commentError = revert.field.comment.key
    ? t_(revert.field.comment.key, revert.field.comment.params)
    : comment.count > comment.max ? t_('The comment cannot be longer than {{max}} characters.', { max: comment.max }) : ''
  return (
    <Box data-testid='revert-confirmation' sx={{ display: 'grid', gap: 1.25, mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2 }}>
      {failed && <Alert severity='warning'>{t_('The revert reasons could not be loaded, so the correction cannot be reverted.')}</Alert>}
      <TextField
        select
        size='small'
        label={t_('Reason for the revert (required)')}
        value={revert.reasonCode}
        disabled={sending}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        error={Boolean(revert.field.reason.key)}
        helperText={revert.field.reason.key ? t_(revert.field.reason.key, revert.field.reason.params) : undefined}
        onChange={(event) => onReason(event.target.value)}
      >
        <option value=''>{t_('Choose a reason…')}</option>
        {reasons.list.map((reason) => <option key={reason.code} value={reason.code}>{t_(reason.label)}</option>)}
      </TextField>
      <TextField
        multiline
        minRows={2}
        size='small'
        label={t_('Comment')}
        placeholder={t_('For example: the worker confirms they did work until the recorded close.')}
        value={revert.comment}
        disabled={sending}
        InputLabelProps={{ shrink: true }}
        error={commentError !== ''}
        helperText={commentError || t_('{{count}} / {{max}} characters', { count: comment.count, max: comment.max })}
        onChange={(event) => onComment(event.target.value)}
      />
      <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
        {action.notes.map((note) => (
          <Typography component='li' variant='body2' color='text.secondary' key={note.key}>{t_(note.key, note.params)}</Typography>
        ))}
      </Box>
      {revert.message.key && <UiMessageAlert message={revert.message} disabled={sending} renderText={renderMessage} onAction={(name) => { if (name === 'TRY_AGAIN') onRetry() }} />}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant='contained' color='error' size='small' disabled={sending || failed || !draft.valid} onClick={onConfirm}>
          {sending ? t_('Reverting…') : t_('Confirm revert')}
        </Button>
        <Button variant='outlined' color='inherit' size='small' disabled={sending} onClick={onCancel}>{t_('Cancel')}</Button>
      </Box>
    </Box>
  )
}

export default RevertConfirmation
